import React, { useEffect } from 'react'
import Svg, { Circle } from 'react-native-svg'
import Animated, {
  useSharedValue,
  useAnimatedProps,
  withTiming,
  Easing,
  withDelay,
} from 'react-native-reanimated'
import { useTheme } from '@ui-kitten/components'
import { ColorValue } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'

interface AnimatedProgressCircleProps {
  progress: number
  progressColor?: ColorValue
  strokeColor?: ColorValue
  strokeOpacity?: number
  size?: number
  strokeWidth?: number
  color?: string
  backgroundColor?: ColorValue
  trackColor?: string
  animationDuration?: number
  animationDelay?: number
  style?: any
}

const AnimatedCircle = Animated.createAnimatedComponent(Circle)

export const AnimatedProgressCircle = ({
  progress,
  size = 53,
  strokeWidth = 6,
  animationDuration = 500,
  animationDelay = 0,
  backgroundColor,
  progressColor,
  strokeColor,
  strokeOpacity,
}: AnimatedProgressCircleProps) => {
  const styles = useStyleSheet(themedStyle)
  const theme = useTheme()

  const normalizedProgress = Math.min(100, Math.max(0, progress))

  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const center = size / 2

  const actualProgressColor = progressColor || theme['theme.success.base']

  const strokeDashoffset = useSharedValue(circumference)

  useEffect(() => {
    const newDashoffset = circumference - (normalizedProgress / 100) * circumference

    strokeDashoffset.value = withDelay(
      animationDelay,
      withTiming(newDashoffset, {
        duration: animationDuration,
        easing: Easing.bezier(0.25, 0.1, 0.25, 1),
      }),
    )
  }, [normalizedProgress, circumference, animationDuration, strokeDashoffset, animationDelay])

  const animatedCircleProps = useAnimatedProps(() => {
    return {
      strokeDashoffset: strokeDashoffset.value,
    }
  })

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={styles.svg}>
      <Circle
        cx={center}
        cy={center}
        r={radius - strokeWidth / 2}
        fill={backgroundColor || styles.circle.backgroundColor}
      />

      <Circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeOpacity={strokeOpacity || 1}
        stroke={strokeColor || styles.grayCircle.backgroundColor}
        strokeWidth={strokeWidth}
      />

      <AnimatedCircle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={actualProgressColor}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        animatedProps={animatedCircleProps}
        strokeLinecap="round"
        rotation="-90"
        origin={`${center}, ${center}`}
      />
    </Svg>
  )
}

const themedStyle = StyleService.create({
  container: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'theme.transparent',
    borderRadius: 1000,
    marginRight: 12,
  },
  svg: {
    position: 'absolute',
  },
  circle: {
    backgroundColor: 'theme.transparent',
  },
  grayCircle: {
    backgroundColor: 'theme.surface.base1',
  },
  iconContainer: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
})
